import { injectable } from 'inversify';
import type {
  ITenantRepository,
  TenantGridDTO,
  TenantFormData,
  TenantMembershipDTO,
  TenantMembershipsResult,
  UpdateClinicLoginPasswordResult,
  CreateTenantResult,
} from '@/domain/repositories/ITenantRepository';
import { Tenant } from '@/domain/entities/Tenant';
import { httpClient } from '@/infrastructure/http/client/httpClient';

@injectable()
export class ApiTenantRepository implements ITenantRepository {
  async getAll(): Promise<TenantGridDTO[]> {
    try {
      const response = await httpClient.get<TenantGridDTO[]>('/tenants');
      return response.data;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      throw error;
    }
  }

  async getById(id: number | string): Promise<Tenant> {
    try {
      const response = await httpClient.get<any>(`/tenants/${encodeURIComponent(String(id))}`);
      const data = response.data;
      
      return Tenant.create(
        data.id as number | string,
        data.nombre,
        data.emailAdministrador,
        data.baseDeDatos || 'default_db',
        data.connectionString || 'default_connection',
        data.timeZoneId || 'America/Argentina/Buenos_Aires',
        new Date(data.fechaActivoUTC),
        data.activo ?? true,
        data.fechaVencimiento ? new Date(data.fechaVencimiento) : undefined,
        data.fechaInactivoUTC ? new Date(data.fechaInactivoUTC) : undefined,
        data.planAdminId
      );
    } catch (error) {
      console.error('Error fetching tenant:', error);
      throw error;
    }
  }

  async create(data: TenantFormData): Promise<CreateTenantResult> {
    try {
      const response = await httpClient.post<any>(
        '/tenants',
        {
          nombre: data.nombre,
          primerNombre: data.primerNombre,
          apellido: data.apellido,
          emailAdministrador: data.emailAdministrador,
          telefono: data.telefono,
          nombreConsultorio: data.nombreConsultorio,
          direccionConsultorio: data.direccionConsultorio,
          telefonoConsultorio: data.telefonoConsultorio,
          timeZoneId: data.timeZoneId,
          fechaVencimiento: data.fechaVencimiento?.toISOString(),
          planAdminId: data.planAdminId,
        },
        { timeout: 180_000 }
      );

      const tenantData = response.data;
      return {
        tenant: Tenant.create(
          tenantData.id as number | string,
          tenantData.nombre,
          tenantData.emailAdministrador,
          tenantData.baseDeDatos || 'default_db',
          tenantData.connectionString || 'default_connection',
          tenantData.timeZoneId || 'America/Argentina/Buenos_Aires',
          new Date(tenantData.fechaActivoUTC),
          tenantData.activo ?? true,
          tenantData.fechaVencimiento ? new Date(tenantData.fechaVencimiento) : undefined,
          tenantData.fechaInactivoUTC ? new Date(tenantData.fechaInactivoUTC) : undefined,
          tenantData.planAdminId
        ),
        credentialsEmailSent: Boolean(tenantData.credentialsEmailSent),
        linkedExistingAccount: Boolean(tenantData.linkedExistingAccount),
        emailWarning: typeof tenantData.emailWarning === 'string' ? tenantData.emailWarning : undefined,
      };
    } catch (error) {
      console.error('Error creating tenant:', error);
      throw error;
    }
  }

  async update(id: number | string, data: TenantFormData): Promise<Tenant> {
    try {
      const response = await httpClient.put<any>(`/tenants/${encodeURIComponent(String(id))}`, {
        nombre: data.nombre,
        logo: data.logo,
        connectionString: data.connectionString,
        emailAdministrador: data.emailAdministrador,
        telefonoContacto: data.telefonoContacto ?? null,
        baseDeDatos: data.baseDeDatos,
        timeZoneId: data.timeZoneId,
        fechaVencimiento: data.fechaVencimiento?.toISOString(),
        planAdminId: data.planAdminId,
        forzarInmediato: data.forzarInmediato,
        motivo: data.motivo,
      });

      const tenantData = response.data;
      return Tenant.create(
        tenantData.id as number | string,
        tenantData.nombre,
        tenantData.emailAdministrador,
        tenantData.baseDeDatos || 'default_db',
        tenantData.connectionString || 'default_connection',
        tenantData.timeZoneId || 'America/Argentina/Buenos_Aires',
        new Date(tenantData.fechaActivoUTC),
        tenantData.activo ?? true,
        tenantData.fechaVencimiento ? new Date(tenantData.fechaVencimiento) : undefined,
        tenantData.fechaInactivoUTC ? new Date(tenantData.fechaInactivoUTC) : undefined,
        tenantData.planAdminId
      );
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  }

  async delete(id: number | string): Promise<void> {
    try {
      await httpClient.delete(`/tenants/${encodeURIComponent(String(id))}`);
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  }

  async changeStatus(id: number | string): Promise<void> {
    try {
      await httpClient.post(`/tenants/${encodeURIComponent(String(id))}/change-status-tenant`);
    } catch (error) {
      console.error('Error changing tenant status:', error);
      throw error;
    }
  }

  async getMyMemberships(email?: string): Promise<TenantMembershipsResult> {
    const response = await httpClient.get<TenantMembershipsResult>(
      '/tenants/my-memberships',
      { params: email ? { email } : undefined }
    );
    return {
      email: response.data.email,
      memberships: response.data.memberships ?? [],
      hasCredentials: Boolean(response.data.hasCredentials),
      credentialsLocked: Boolean(response.data.credentialsLocked),
    };
  }

  async addMyMembership(tenantId: string | number, email?: string): Promise<TenantMembershipDTO> {
    const response = await httpClient.post<TenantMembershipDTO>('/tenants/my-memberships', {
      tenantId,
      ...(email ? { email } : {}),
    });
    return response.data;
  }

  async updateMyMembership(
    id: string,
    patch: { tenantId?: string | number; activo?: boolean },
    email?: string
  ): Promise<TenantMembershipDTO> {
    const response = await httpClient.patch<TenantMembershipDTO>(
      `/tenants/my-memberships/${encodeURIComponent(id)}`,
      { ...patch, ...(email ? { email } : {}) }
    );
    return response.data;
  }

  async changeMembershipsEmail(
    fromEmail: string,
    toEmail: string
  ): Promise<TenantMembershipsResult> {
    const response = await httpClient.patch<TenantMembershipsResult>(
      '/tenants/my-memberships/email',
      { fromEmail, email: toEmail }
    );
    return {
      email: response.data.email,
      memberships: response.data.memberships ?? [],
      hasCredentials: Boolean(response.data.hasCredentials),
      credentialsLocked: Boolean(response.data.credentialsLocked),
    };
  }

  async updateClinicLoginPassword(
    email: string,
    password: string,
    sendEmail?: boolean
  ): Promise<UpdateClinicLoginPasswordResult> {
    const response = await httpClient.put<UpdateClinicLoginPasswordResult>(
      '/tenants/my-memberships/password',
      { email, password, sendEmail: Boolean(sendEmail) }
    );
    return {
      email: response.data.email,
      created: Boolean(response.data.created),
      emailSent: Boolean(response.data.emailSent),
      emailWarning: response.data.emailWarning,
      hasCredentials: Boolean(response.data.hasCredentials),
      credentialsLocked: Boolean(response.data.credentialsLocked),
    };
  }

  async deleteMyMembership(id: string, email?: string): Promise<void> {
    await httpClient.delete(`/tenants/my-memberships/${encodeURIComponent(id)}`, {
      params: email ? { email } : undefined,
    });
  }
}
